import {
  useEffect,
  useRef,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  Canvas,
  FabricImage,
  FabricText,
  Rect,
  Group,
  Point,
  Circle,
} from "fabric";
import { useFetchData } from "../helpers/FetchDataHelpers";
import { UserContext } from "../context/UserContext";
import { toast } from "react-toastify";
import { Spinner, Card, ListGroup, ListGroupItem } from "react-bootstrap";
import Hammer from "hammerjs";

const FILL_FILTERED_COLOR = "rgba(235, 152, 92, 0.7)";
const FILL_UNFILTERED_COLOR = "rgba(0, 24, 105, 0.76)";
const GONDOLA_FILL_FILTERED_COLOR = "rgba(235, 152, 92, 0.7)";
const GONDOLA_FILL_UNFILTERED_COLOR = "rgba(0, 0, 0, 0.7)";
const STROKE_DEFAULT_COLOR = "white";
const STROKE_DEFAULT_WIDTH = 1;
const STROKE_SELECTED_COLOR = "yellow";
const STROKE_SELECTED_WIDTH = 4;
const CLICK_DRAG_THRESHOLD_PX = 10;
// The picker card's size is capped by these two constants (height with
// internal scroll, width fixed), and both clamps below are measured against
// the same constants, so the clamp and the actual rendered size can never
// disagree with each other. A click near any edge of the viewport can then
// never anchor the card past a point where part of it would render off
// screen, no matter how many overlapping cameras there are.
const OVERLAP_PICKER_MAX_HEIGHT_PX = 260;
const OVERLAP_PICKER_WIDTH_PX = 220;
const OVERLAP_PICKER_EDGE_MARGIN_PX = 20;
const MARKER_RADIUS_PX = 8;
// Multiplier applied to a marker's radius on screen when deciding whether a
// click is ambiguous between two or more camera markers. Wide enough to
// catch markers that are only a few pixels apart on screen (two cameras
// close enough that neither is reliably clickable even at max zoom), without
// pulling unrelated nearby markers into a disambiguation popover for what is
// otherwise an unambiguous click.
const MARKER_HIT_TOLERANCE_FACTOR = 2.2;

// Cameras are grouped into circles by position (mirrors the backend's own
// grouping key), so position doubles as a stable identity across polls.
// Caveat: if a camera is removed and a different one appears at the exact same
// pos_x/pos_y between polls, this key conflates them into the same marker —
// the reconciliation patches it in place instead of recreating it, so a
// canvas selection made before the swap keeps pointing at (now stale) data
// until the user re-selects. Accepted as a rare edge case, not worth the extra
// complexity of a swap-detection check given how unlikely it is in practice.
//
// camera_ids is a required list[int] on the backend model (CamerasCircleModel,
// store-deployment-api), never empty, with a single construction site that
// always seeds it with at least one id. Code in this file reads it directly
// without guarding for it being missing or empty on purpose.
const cameraKey = (cam) => `${cam.pos_x},${cam.pos_y}`;
const gondolaKey = (gondola) => gondola.gondola_id;

// Camera markers within MARKER_HIT_TOLERANCE_FACTOR times their current
// radius on screen of `scenePoint` are candidates for a click, nearest
// first. Only depends on the plain {left, top, visible} shape (not fabric or
// the canvas itself), so this geometry can be unit tested without mounting
// the canvas or mocking fabric.
export const findOverlappingMarkers = (markers, scenePoint, zoom) => {
  const toleranceRadius =
    (MARKER_RADIUS_PX / zoom) * MARKER_HIT_TOLERANCE_FACTOR;

  return markers
    .filter((marker) => marker.visible !== false)
    .map((marker) => ({
      marker,
      distance: Math.hypot(
        marker.left - scenePoint.x,
        marker.top - scenePoint.y,
      ),
    }))
    .filter(({ distance }) => distance <= toleranceRadius)
    .sort((a, b) => a.distance - b.distance)
    .map(({ marker }) => marker);
};

// Screen coordinates for positioning an HTML overlay at a fabric pointer
// event. A TouchEvent has no top level clientX/clientY (the coordinates
// live on changedTouches), unlike a MouseEvent, so this normalizes both
// instead of assuming the mouse shape everywhere this file already
// supports touch (Hammer gestures, touchAction, the touchend listener in
// the gesture effect below). Exported and testable on its own, same
// reasoning as findOverlappingMarkers above.
export const getClientPoint = (e) => {
  const touch = e.changedTouches?.[0] ?? e.touches?.[0];
  if (touch) return { x: touch.clientX, y: touch.clientY };
  return { x: e.clientX, y: e.clientY };
};

export const StoreBlueprint = (props) => {
  const canvasRef = useRef(null);
  const fabricCanvas = useRef(null);
  const storeBlueprintImageRef = useRef(null);
  const cameraMarkers = useRef([]);
  const cameraMarkersByKey = useRef(new Map());
  const gondolaBoxesByKey = useRef(new Map());
  const visibilityFrameRef = useRef(null);
  const fetchData = useFetchData();
  const { userenv } = useContext(UserContext);

  const {
    canvasRefParent,
    selectedDeployment,
    cameras,
    gondolas,
    setSelectedObject,
    selectedCameraId,
  } = props;
  const deploymentId = selectedDeployment.deployment_id;

  const [storeImageLoaded, setStoreImageLoaded] = useState(false);
  // Not null while a click landed near 2+ camera markers at once: { x, y }
  // are viewport coordinates for positioning the picker, `items` the
  // candidate markers, nearest first.
  const [overlapCandidates, setOverlapCandidates] = useState(null);

  // Read from effects that shouldn't re-run just because selection changed
  // (only the initial stroke of a newly created marker needs this value).
  const selectedCameraIdRef = useRef(selectedCameraId);
  useEffect(() => {
    selectedCameraIdRef.current = selectedCameraId;
  }, [selectedCameraId]);

  useEffect(() => {
    console.log("Initializing fabric canvas...");
    const canvas = new Canvas(canvasRef.current, {
      selection: false,
      allowTouchScrolling: true,
    });
    fabricCanvas.current = canvas;

    fabricCanvas.current.upperCanvasEl.style.touchAction = "none"; // Disable touch scrolling on the canvas
    fabricCanvas.current.upperCanvasEl.style.msTouchAction = "none"; // Disable touch scrolling on the canvas for IE/Edge

    if (canvasRefParent) {
      canvasRefParent.current = canvas;
    }

    // Make canvas fullscreen
    const resizeCanvas = () => {
      canvas.setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      canvas.renderAll();
    };
    resizeCanvas(); // Set initial size

    // Resize canvas on window resize
    window.addEventListener("resize", resizeCanvas);

    return () => {
      if (canvas.__renderAllFrame) {
        cancelAnimationFrame(canvas.__renderAllFrame);
        canvas.__renderAllFrame = null;
      }
      if (canvas.__coordsUpdateFrame) {
        cancelAnimationFrame(canvas.__coordsUpdateFrame);
        canvas.__coordsUpdateFrame = null;
      }
      if (visibilityFrameRef.current) {
        cancelAnimationFrame(visibilityFrameRef.current);
        visibilityFrameRef.current = null;
      }
      canvas.dispose();
      window.removeEventListener("resize", resizeCanvas);
      // Without this, canvasRefParent.current would still point at a disposed
      // canvas after unmount. GondolaDetails, for example, awaits a DELETE
      // request before calling hidePanel(), which can resolve post-unmount.
      if (canvasRefParent) {
        canvasRefParent.current = null;
      }
    };
  }, [canvasRefParent]);

  useEffect(() => {
    if (!fabricCanvas.current) return;

    const cameraMarkersMap = cameraMarkersByKey.current;
    const gondolaBoxesMap = gondolaBoxesByKey.current;

    setStoreImageLoaded(false);
    const fetchStoreImage = async () => {
      const url = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${deploymentId}/image`;
      const { isResponseOK, data, errorMessage } = await fetchData(
        url,
        "GET",
        true,
      );
      if (isResponseOK && data) {
        const imageBlob = new Blob([data], { type: "image/jpg" });
        const imageUrl = URL.createObjectURL(imageBlob);
        const img = new Image();
        img.src = imageUrl;
        img.onload = async () => {
          const fabricImg = await FabricImage.fromURL(img.src);
          fabricImg.set({
            left: 0,
            top: 0,
            originX: "left",
            originY: "top",
            selectable: false,
            evented: false,
          });
          storeBlueprintImageRef.current = fabricImg;
          if (fabricCanvas.current.backgroundImage) {
            fabricCanvas.current.backgroundImage.dispose();
            fabricCanvas.current.backgroundImage = null;
          }
          fabricCanvas.current.backgroundImage = fabricImg;
          fabricCanvas.current.renderAll();
          centerMap(fabricCanvas.current, fabricImg);
          setStoreImageLoaded(true);
        };
      } else {
        toast.error(`Error fetching store image: ${errorMessage}`, {
          autoClose: 3000,
          hideProgressBar: true,
        });
        console.error("Error fetching store image:", errorMessage);
      }
    };
    fetchStoreImage();

    // Cleanup function to remove event listeners and clear canvas
    return () => {
      if (
        fabricCanvas.current &&
        fabricCanvas.current.getObjects().length > 0
      ) {
        if (fabricCanvas.current.backgroundImage) {
          fabricCanvas.current.backgroundImage.dispose();
          fabricCanvas.current.backgroundImage = null;
        }
        fabricCanvas.current.clear();
        fabricCanvas.current.off();
      }
      // The canvas above was wiped, so any markers/boxes the reconciliation
      // effect was tracking no longer exist — forget them so the next run
      // recreates rather than tries to patch disposed objects.
      cameraMarkersMap.clear();
      gondolaBoxesMap.clear();
      cameraMarkers.current = [];
    };
  }, [deploymentId, fetchData, userenv]);

  const throttledRenderAll = useCallback((canvas) => {
    if (!canvas.__renderAllFrame) {
      canvas.__renderAllFrame = requestAnimationFrame(() => {
        canvas.renderAll();
        canvas.__renderAllFrame = null;
      });
    }
  }, []);

  const updateCameraVisibility = useCallback((canvas) => {
    const zoom = canvas.getZoom();
    const [translateX, translateY] = [
      canvas.viewportTransform[4],
      canvas.viewportTransform[5],
    ];
    const [w, h] = [canvas.getWidth(), canvas.getHeight()];

    cameraMarkers.current.forEach((marker) => {
      const screenX = marker.left * zoom + translateX;
      const screenY = marker.top * zoom + translateY;

      const isVisible =
        screenX >= -40 &&
        screenX <= w + 40 &&
        screenY >= -40 &&
        screenY <= h + 40;

      marker.visible = isVisible;
    });
  }, []);

  const debouncedUpdateCameraVisibility = useMemo(() => {
    return (canvas) => {
      if (visibilityFrameRef.current) {
        cancelAnimationFrame(visibilityFrameRef.current);
      }
      visibilityFrameRef.current = requestAnimationFrame(() => {
        updateCameraVisibility(canvas);
        visibilityFrameRef.current = null;
      });
    };
  }, [updateCameraVisibility]);

  // Apply panning but restrict it within image boundaries
  const applyPanWithLimits = useCallback(
    (canvas, deltaX, deltaY) => {
      if (!storeBlueprintImageRef.current) return;

      const img = storeBlueprintImageRef.current;
      const viewportTransform = canvas.viewportTransform;
      const zoom = canvas.getZoom();

      // Current pan position
      let newX = viewportTransform[4] + deltaX;
      let newY = viewportTransform[5] + deltaY;

      // Map boundaries
      const minX = Math.min(0, -(img.width * zoom - canvas.getWidth()));
      const minY = Math.min(0, -(img.height * zoom - canvas.getHeight()));
      const maxX = Math.max(0, canvas.getWidth() - img.width * zoom);
      const maxY = Math.max(0, canvas.getHeight() - img.height * zoom);

      newX = Math.min(Math.max(newX, minX), maxX);
      newY = Math.min(Math.max(newY, minY), maxY);

      // Apply new pan values
      canvas.viewportTransform[4] = newX;
      canvas.viewportTransform[5] = newY;

      debouncedUpdateCameraVisibility(canvas);
      throttledRenderAll(canvas);

      // We debounce the setCoords for optimization
      if (!canvas.__coordsUpdateFrame) {
        canvas.__coordsUpdateFrame = requestAnimationFrame(() => {
          // This is important for the selection of objects to work correctly
          canvas.getObjects().forEach((obj) => {
            if (obj.visible) obj.setCoords();
          });
          canvas.__coordsUpdateFrame = null;
        });
      }
    },
    [debouncedUpdateCameraVisibility, throttledRenderAll],
  );

  // Canvas gestures/interaction — set up once per loaded image, independent of
  // cameras/gondolas data. Previously this ran on every cameras/gondolas change
  // (i.e. every poll) without ever detaching its own canvas listeners, so each
  // poll stacked a duplicate "selection:created"/"mouse:wheel" handler on top
  // of the last.
  useEffect(() => {
    if (!fabricCanvas.current || !storeImageLoaded) return;

    const fabricCanvasCurrent = fabricCanvas.current;

    let lastZoom = fabricCanvasCurrent.getZoom();
    let isPinching = false; // Track pinch state
    let lastPosX = 0;
    let lastPosY = 0;
    let zoomAnimationFrame = null;
    let mouseDownPoint = null;

    const hammer = new Hammer(fabricCanvasCurrent.upperCanvasEl);

    // Configure Pan and Pinch recognizers
    const pan = new Hammer.Pan({ direction: Hammer.DIRECTION_ALL });
    const pinch = new Hammer.Pinch();

    hammer.add([pan, pinch]);

    // Prioritize pinch over pan
    pan.requireFailure([pinch]);

    hammer.on("panstart", (e) => {
      if (isPinching) return; // Ignore panstart during pinch
      lastPosX = e.deltaX;
      lastPosY = e.deltaY;
    });

    hammer.on("panmove", (e) => {
      if (isPinching) return; // Ignore panmove during pinch
      const deltaX = e.deltaX - lastPosX;
      const deltaY = e.deltaY - lastPosY;
      applyPanWithLimits(fabricCanvasCurrent, deltaX, deltaY);
      lastPosX = e.deltaX;
      lastPosY = e.deltaY;
    });

    hammer.on("pinchstart", () => {
      isPinching = true;
      lastZoom = fabricCanvasCurrent.getZoom();
      // Discard any pre-pinch mousedown the click guard below recorded, so
      // the pinch's eventual mouseup isn't measured against it.
      mouseDownPoint = null;
    });

    hammer.on("pinchmove", (e) => {
      if (!isPinching) return; // Ignore pinchmove if not pinching
      const zoomDelta = e.scale - 1;
      const zoomSpeed = 0.05;
      const newZoom = lastZoom * (1 + zoomDelta * zoomSpeed);
      const clampedZoom = Math.max(0.5, Math.min(newZoom, 3));

      // Get the center of the viewport (not the gesture)
      const viewportCenterX = fabricCanvasCurrent.getWidth() / 2;
      const viewportCenterY = fabricCanvasCurrent.getHeight() / 2;

      // Zoom to viewport center
      throttledApplyZoom(fabricCanvasCurrent, clampedZoom, {
        x: viewportCenterX,
        y: viewportCenterY,
      });

      lastZoom = clampedZoom;
    });

    // "pinchcancel" (Hammer's event for a pinch interrupted by e.g. touchcancel
    // on a tablet) must reset isPinching too, or it stays stuck at true and
    // permanently disables pan and the click-vs-drag guard below.
    hammer.on("pinchend pinchcancel", () => {
      isPinching = false;
    });

    const preventGhostClick = (e) => e.preventDefault();
    fabricCanvasCurrent.upperCanvasEl.addEventListener(
      "touchend",
      preventGhostClick,
      { passive: false },
    );

    // Attach selection events
    const handleSelection = (event) => {
      setSelectedObject(event.selected[0]);
    };
    fabricCanvasCurrent.on("selection:created", handleSelection);
    fabricCanvasCurrent.on("selection:updated", handleSelection);

    // Click-vs-drag guard: fabric's "activeOn: up" (set on markers above)
    // only checks what's under the cursor at mouseup, ignoring how far the
    // pointer travelled. A pan gesture ending on top of a marker would
    // still open its panel. We track that distance and undo the selection
    // when it was clearly a drag. Discarding unconditionally (not only when
    // this gesture caused the selection) is safe because DetailsPanel's
    // Offcanvas backdrop covers the full viewport, so a drag can never start
    // while a panel is already open.
    const handleMouseDownForClickGuard = (opt) => {
      // Keeps the invariant handleMouseUpForClickGuard relies on: mouseDownPoint
      // is only ever non-null when isPinching is false.
      if (isPinching) return;
      mouseDownPoint = fabricCanvasCurrent.getViewportPoint(opt.e);
    };

    const handleMouseUpForClickGuard = (opt) => {
      // No mousedown was tracked: either stray noise (nothing selected, so
      // this is a no-op), a pinch still in progress, or the tail of a pinch
      // that just ended (mouseDownPoint is cleared in pinchstart and never
      // repopulated while isPinching, since handleMouseDownForClickGuard
      // above skips it too). Fabric only fires mouseup once every finger has
      // lifted, by which point activeOn:"up" may have already selected
      // whatever was under the last one. Checking mouseDownPoint alone (not
      // isPinching) means this doesn't depend on whether Hammer's pinchend
      // fires before or after fabric's own mouseup for that same touchend.
      // Same safety as the drag case below applies either way.
      if (!mouseDownPoint) {
        fabricCanvasCurrent.discardActiveObject();
        fabricCanvasCurrent.requestRenderAll();
        setSelectedObject(null);
        setOverlapCandidates(null);
        return;
      }

      const upPoint = fabricCanvasCurrent.getViewportPoint(opt.e);
      const distance = Math.hypot(
        upPoint.x - mouseDownPoint.x,
        upPoint.y - mouseDownPoint.y,
      );
      mouseDownPoint = null;
      if (distance > CLICK_DRAG_THRESHOLD_PX) {
        fabricCanvasCurrent.discardActiveObject();
        fabricCanvasCurrent.requestRenderAll();
        setSelectedObject(null);
        setOverlapCandidates(null);
        return;
      }

      // A confirmed click (not a drag). Fabric's own hit testing
      // (activeOn: "up" on each marker, above) may already have selected
      // whichever camera marker happens to be topmost under the cursor.
      // When two or more are within click tolerance of each other, override
      // that single pick with an explicit choice instead of silently
      // favoring the one on top.
      const scenePoint = fabricCanvasCurrent.getScenePoint(opt.e);
      const overlapping = findOverlappingMarkers(
        cameraMarkers.current,
        scenePoint,
        fabricCanvasCurrent.getZoom(),
      );
      if (overlapping.length > 1) {
        fabricCanvasCurrent.discardActiveObject();
        fabricCanvasCurrent.requestRenderAll();
        setSelectedObject(null);
        const clientPoint = getClientPoint(opt.e);
        setOverlapCandidates({
          x: clientPoint.x,
          y: clientPoint.y,
          items: overlapping,
        });
      } else {
        setOverlapCandidates(null);
      }
    };

    fabricCanvasCurrent.on("mouse:down", handleMouseDownForClickGuard);
    fabricCanvasCurrent.on("mouse:up", handleMouseUpForClickGuard);

    const applyZoom = (canvas, zoom, pointer) => {
      const zoomPoint = new Point(pointer.x, pointer.y);
      canvas.zoomToPoint(zoomPoint, zoom);
      applyPanWithLimits(canvas, 0, 0); // Adjust panning limits after zooming
      const markerScale = 1 / zoom;
      // Adjust markers to maintain their size
      cameraMarkers.current.forEach((marker) => {
        // Optimize marker scaling
        // by avoiding unnecessary updates when the scale difference is small
        if (Math.abs(marker.scaleX - markerScale) > 0.01) {
          marker.scaleX = markerScale;
          marker.scaleY = markerScale;
        }
      });
      debouncedUpdateCameraVisibility(canvas);
      throttledRenderAll(canvas);
    };

    const throttledApplyZoom = (canvas, zoom, pointer) => {
      if (zoomAnimationFrame) return;
      zoomAnimationFrame = requestAnimationFrame(() => {
        applyZoom(canvas, zoom, pointer);
        zoomAnimationFrame = null;
      });
    };

    const handleMouseWheel = (event) => {
      if (event.e.button !== 0) return;
      event.e.preventDefault(); // Prevent browser scroll
      event.e.stopPropagation();

      const zoomFactor = 1.3; // Zoom speed
      const prevZoom = fabricCanvasCurrent.getZoom();
      let newZoom = prevZoom;
      const pointer = fabricCanvasCurrent.getScenePoint(event.e);
      if (event.e.deltaY < 0) {
        // Zoom in
        newZoom *= zoomFactor;
      } else {
        // Zoom out
        newZoom /= zoomFactor;
      }
      newZoom = Math.max(0.5, Math.min(newZoom, 3));

      // Apply zoom centered on the cursor
      if (newZoom !== prevZoom) {
        throttledApplyZoom(fabricCanvasCurrent, newZoom, pointer);
      }
    };
    fabricCanvasCurrent.on("mouse:wheel", handleMouseWheel);

    return () => {
      if (zoomAnimationFrame) {
        cancelAnimationFrame(zoomAnimationFrame);
      }
      hammer.destroy();
      fabricCanvasCurrent.upperCanvasEl?.removeEventListener(
        "touchend",
        preventGhostClick,
      );
      fabricCanvasCurrent.off("selection:created", handleSelection);
      fabricCanvasCurrent.off("selection:updated", handleSelection);
      fabricCanvasCurrent.off("mouse:down", handleMouseDownForClickGuard);
      fabricCanvasCurrent.off("mouse:up", handleMouseUpForClickGuard);
      fabricCanvasCurrent.off("mouse:wheel", handleMouseWheel);
    };
  }, [
    storeImageLoaded,
    setSelectedObject,
    applyPanWithLimits,
    debouncedUpdateCameraVisibility,
    throttledRenderAll,
  ]);

  // Reconciles camera markers/gondola boxes with the latest data: existing
  // objects are patched in place (fill color, data, position) instead of being
  // destroyed and recreated, so an in-progress canvas selection survives a
  // poll. Objects are only added/removed when a device actually appears in or
  // disappears from the incoming arrays.
  useEffect(() => {
    if (!fabricCanvas.current || !storeImageLoaded) return;

    const canvas = fabricCanvas.current;

    const nextCameraKeys = new Set(cameras.map(cameraKey));
    cameraMarkersByKey.current.forEach((marker, key) => {
      if (!nextCameraKeys.has(key)) {
        canvas.remove(marker);
        cameraMarkersByKey.current.delete(key);
      }
    });

    cameras.forEach((cam) => {
      const key = cameraKey(cam);
      const fill = cam.filtered ? FILL_FILTERED_COLOR : FILL_UNFILTERED_COLOR;
      const existingMarker = cameraMarkersByKey.current.get(key);

      if (existingMarker) {
        existingMarker.camera = cam;
        existingMarker.set({ left: cam.pos_x, top: cam.pos_y, fill });
        existingMarker.setCoords();
        return;
      }

      const markerScale = 1 / canvas.getZoom();
      const isSelected =
        selectedCameraIdRef.current != null &&
        cam.camera_ids.includes(selectedCameraIdRef.current);
      const marker = new Circle({
        objType: "camera",
        camera: cam,
        left: cam.pos_x,
        top: cam.pos_y,
        radius: MARKER_RADIUS_PX,
        fill,
        stroke: isSelected ? STROKE_SELECTED_COLOR : STROKE_DEFAULT_COLOR,
        strokeWidth: isSelected ? STROKE_SELECTED_WIDTH : STROKE_DEFAULT_WIDTH,
        originX: "center",
        originY: "center",
        selectable: true,
        evented: true,
        // Select on mouseup, not mousedown, so a mousedown that turns into a
        // pan gesture (dragging the map from on top of this marker) gets a
        // chance to run before we decide whether this was a click.
        activeOn: "up",
        hasControls: false,
        hoverCursor: "pointer",
        lockMovementX: true,
        lockMovementY: true,
        borderColor: "orange",
        borderScaleFactor: 3,
        scaleX: markerScale,
        scaleY: markerScale,
      });
      canvas.add(marker);
      cameraMarkersByKey.current.set(key, marker);
    });

    cameraMarkers.current = Array.from(cameraMarkersByKey.current.values());

    const nextGondolaKeys = new Set(gondolas.map(gondolaKey));
    gondolaBoxesByKey.current.forEach((group, key) => {
      if (!nextGondolaKeys.has(key)) {
        canvas.remove(group);
        gondolaBoxesByKey.current.delete(key);
      }
    });

    gondolas.forEach((gondola) => {
      const key = gondolaKey(gondola);
      const fill = gondola.filtered
        ? GONDOLA_FILL_FILTERED_COLOR
        : GONDOLA_FILL_UNFILTERED_COLOR;
      const gondolaBoxText = gondola.gondola_external_id
        ? gondola.gondola_external_id
        : "";
      const existingGroup = gondolaBoxesByKey.current.get(key);

      if (existingGroup) {
        existingGroup.gondola = gondola;
        existingGroup.set({
          left: gondola.img_pos_x,
          top: gondola.img_pos_y,
          angle: gondola.yaw,
        });
        existingGroup.gondolaBox.set({
          fill,
          width: gondola.dimensions_x,
          height: gondola.dimensions_y,
        });
        existingGroup.gondolaText.set({
          text: gondolaBoxText,
          angle: -gondola.yaw,
        });
        existingGroup.setCoords();
        return;
      }

      const box = new Rect({
        originX: "center",
        originY: "center",
        width: gondola.dimensions_x,
        height: gondola.dimensions_y,
        fill,
        strokeWidth: 2,
      });

      const text = new FabricText(gondolaBoxText, {
        fontSize: 10,
        fontFamily: "Arial",
        fill: "white",
        angle: -gondola.yaw,
        originX: "center",
        originY: "center",
      });

      const group = new Group([box, text], {
        objType: "gondola",
        gondola: gondola,
        originX: "center",
        originY: "center",
        left: gondola.img_pos_x, // Example position on the map
        top: gondola.img_pos_y,
        angle: gondola.yaw,
        selectable: true,
        evented: true,
        // See the matching comment on the camera marker above.
        activeOn: "up",
        hoverCursor: "pointer",
        hasControls: false,
        lockMovementX: true,
        lockMovementY: true,
        borderColor: "orange",
        borderScaleFactor: 3,
      });
      // Kept so the fill/text can be patched later without recreating the group.
      group.gondolaBox = box;
      group.gondolaText = text;

      canvas.add(group);
      gondolaBoxesByKey.current.set(key, group);
    });

    // Camera markers must stay clickable above gondola boxes regardless of
    // insertion order, since cameras and gondolas can each arrive on a later
    // poll after the other is already on the canvas.
    cameraMarkersByKey.current.forEach((marker) => {
      canvas.bringObjectToFront(marker);
    });

    // New/repositioned markers default to visible — recompute against the
    // current viewport so a marker that appears (or moves) off-screen isn't
    // left showing, and one that appears on-screen isn't left hidden, since
    // visibility is otherwise only recomputed on pan/zoom.
    debouncedUpdateCameraVisibility(canvas);
    throttledRenderAll(canvas);
  }, [
    storeImageLoaded,
    cameras,
    gondolas,
    debouncedUpdateCameraVisibility,
    throttledRenderAll,
  ]);

  useEffect(() => {
    if (!fabricCanvas.current || !storeImageLoaded) return;
    if (!cameraMarkers.current || cameraMarkers.current.length === 0) return;

    let selectedMarker = null;
    cameraMarkers.current.forEach((marker) => {
      const cam = marker.camera;
      const isSelected =
        selectedCameraId != null &&
        cam &&
        cam.camera_ids.includes(selectedCameraId);

      if (isSelected) {
        marker.set({
          stroke: STROKE_SELECTED_COLOR,
          strokeWidth: STROKE_SELECTED_WIDTH,
        });
        selectedMarker = marker;
      } else {
        marker.set({
          stroke: STROKE_DEFAULT_COLOR,
          strokeWidth: STROKE_DEFAULT_WIDTH,
        });
      }
    });

    if (selectedMarker) {
      const canvas = fabricCanvas.current;
      const zoom = canvas.getZoom();
      const targetTx = canvas.getWidth() / 2 - selectedMarker.left * zoom;
      const targetTy = canvas.getHeight() / 2 - selectedMarker.top * zoom;
      const deltaX = targetTx - canvas.viewportTransform[4];
      const deltaY = targetTy - canvas.viewportTransform[5];
      applyPanWithLimits(canvas, deltaX, deltaY);
    } else {
      fabricCanvas.current.renderAll();
    }
  }, [selectedCameraId, cameras, storeImageLoaded, applyPanWithLimits]);

  // Closes the disambiguation popover on Escape. Scoped to only listen while
  // it's actually open, same reasoning as the other event listeners here:
  // don't hold a global listener for the whole component lifetime.
  useEffect(() => {
    if (!overlapCandidates) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOverlapCandidates(null);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [overlapCandidates]);

  // The reconciliation effect above removes a marker from the canvas as
  // soon as its camera drops out of a poll. If that happens while the
  // picker is still open, closes it rather than leaving a candidate that
  // points at a marker no longer on the canvas, which picking would
  // otherwise select as if the camera still existed. Recomputes the current
  // keys straight from the cameras prop, not from the cameraMarkersByKey ref
  // that other effect maintains, so this check has no dependency on that
  // other effect having already run first within the same commit.
  useEffect(() => {
    if (!overlapCandidates) return;
    const currentKeys = new Set(cameras.map(cameraKey));
    const stillValid = overlapCandidates.items.every((marker) =>
      currentKeys.has(cameraKey(marker.camera)),
    );
    if (!stillValid) setOverlapCandidates(null);
  }, [cameras, overlapCandidates]);

  // Any candidate the picker is showing belongs to whichever deployment was
  // selected when it opened. Closing on every deploymentId change, instead
  // of folding this into the cameras validity check above, sidesteps a
  // timing gap: cameras only updates once the new deployment's fetch
  // resolves (see fetchCameras in StoreDeployment.jsx), which can lag well
  // behind the image effect below already clearing this canvas the moment
  // deploymentId changes.
  useEffect(() => {
    setOverlapCandidates(null);
  }, [deploymentId]);

  const handleSelectOverlapCandidate = (marker) => {
    // A direct click leaves fabric's own active object border (borderColor
    // "orange", set on every marker) around whichever one was picked, so the
    // user can see which dot on the map they selected. The picker discarded
    // that active object when the ambiguity was first detected, so it has
    // to be restored here or two markers close enough to need this picker
    // in the first place would give no way to tell which one was chosen.
    const canvas = fabricCanvas.current;
    if (canvas) {
      canvas.setActiveObject(marker);
      canvas.requestRenderAll();
    }
    // setActiveObject above also fires fabric's own "selection:created",
    // which the gesture effect already forwards to setSelectedObject. Kept
    // as an explicit call anyway so the selection stays correct even if
    // that event configuration ever changes, and so it holds in tests,
    // where the mocked canvas does not simulate real fabric events.
    setSelectedObject(marker);
    setOverlapCandidates(null);
  };

  // Center the map in the viewport
  const centerMap = (canvas, img) => {
    const viewportWidth = canvas.getWidth();
    const viewportHeight = canvas.getHeight();
    const mapWidth = img.width;
    const mapHeight = img.height;

    const centerX = (viewportWidth - mapWidth) / 2;
    const centerY = (viewportHeight - mapHeight) / 2;

    canvas.viewportTransform[4] = centerX;
    canvas.viewportTransform[5] = centerY;
    canvas.renderAll();
  };

  return (
    <div style={{ position: "relative", width: "100vw", height: "100vh" }}>
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          touchAction: "none",
          msTouchAction: "none",
        }}
      />
      {!storeImageLoaded && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          <Spinner
            animation="border"
            variant="light"
            style={{ width: "5rem", height: "5rem", borderWidth: "0.5rem" }}
          />
        </div>
      )}
      {overlapCandidates && (
        <>
          <div
            data-testid="overlap-picker-backdrop"
            onClick={() => setOverlapCandidates(null)}
            style={{ position: "fixed", inset: 0, zIndex: 1099 }}
          />
          <Card
            data-testid="overlap-picker"
            style={{
              position: "fixed",
              // Math.max(0, ...) keeps this from going negative on a
              // viewport narrower or shorter than the card plus its margin
              // (a small handheld device), which would otherwise anchor the
              // card partly off screen past the left or top edge the same
              // way the bug this component already guards against did past
              // the right and bottom.
              left: Math.max(
                0,
                Math.min(
                  overlapCandidates.x,
                  window.innerWidth -
                    OVERLAP_PICKER_WIDTH_PX -
                    OVERLAP_PICKER_EDGE_MARGIN_PX,
                ),
              ),
              top: Math.max(
                0,
                Math.min(
                  overlapCandidates.y,
                  window.innerHeight -
                    OVERLAP_PICKER_MAX_HEIGHT_PX -
                    OVERLAP_PICKER_EDGE_MARGIN_PX,
                ),
              ),
              width: OVERLAP_PICKER_WIDTH_PX,
              maxHeight: OVERLAP_PICKER_MAX_HEIGHT_PX,
              overflowY: "auto",
              zIndex: 1100,
            }}
          >
            <Card.Header className="py-2 px-3 small text-muted">
              {overlapCandidates.items.length} cameras here
            </Card.Header>
            <ListGroup variant="flush">
              {/* Labeled by position in the list, not by camera_ids, since
                  camera_ids is an internal database id with no meaning to
                  the user. The real backend_id only exists on the per camera
                  fetch CameraDetails already does once a choice is made. */}
              {overlapCandidates.items.map((marker, index) => (
                <ListGroupItem
                  key={marker.camera.camera_ids.join(",")}
                  action
                  onClick={() => handleSelectOverlapCandidate(marker)}
                >
                  Camera {index + 1}
                </ListGroupItem>
              ))}
            </ListGroup>
          </Card>
        </>
      )}
      {props.children}
    </div>
  );
};

export default StoreBlueprint;
